import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';

import { BUFFERED_PERIOD_MONTHS } from '../../constants/sizes';
import { calculateBufferedPeriod } from '../../helpers/date';
import { VisibleDateRange } from '../../types';

/**
 * Viewport → date-range math shared by the filter, group-switch and preference
 * actions: which dates are visible, what buffered period should a reload fetch,
 * and what exact scroll position should be restored afterwards.
 */

export const getVisibleViewportRange = (
  windowDates: dayjs.Dayjs[],
  visibleDateRange: VisibleDateRange,
  fallbackDate: dayjs.Dayjs
): { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs } => {
  const hasDates = windowDates.length > 0;
  const startIndex = hasDates
    ? Math.min(Math.max(visibleDateRange.startIndex, 0), windowDates.length - 1)
    : -1;
  const endIndex = hasDates
    ? Math.min(Math.max(visibleDateRange.endIndex, startIndex), windowDates.length - 1)
    : -1;

  const startDate = startIndex >= 0 ? windowDates[startIndex] : fallbackDate;
  const endDate = endIndex >= 0 ? windowDates[endIndex] : fallbackDate;

  return {
    startDate,
    endDate,
  };
};

export const getBufferedVisibleViewportPeriod = (
  windowDates: dayjs.Dayjs[],
  visibleDateRange: VisibleDateRange,
  fallbackDate: dayjs.Dayjs
): { startDate: string; endDate: string } => {
  const visibleViewportRange = getVisibleViewportRange(windowDates, visibleDateRange, fallbackDate);

  return calculateBufferedPeriod(
    visibleViewportRange.startDate.format(DATE_KEY_FORMAT),
    visibleViewportRange.endDate.format(DATE_KEY_FORMAT),
    BUFFERED_PERIOD_MONTHS
  );
};

/**
 * Current horizontal scroll position (px) of the planner scroll container, or
 * null if it isn't mounted. Read directly from the DOM (the same `planner-container`
 * id the scroll-to-date helpers use) so filter actions can snapshot the EXACT
 * viewport position to restore after the reload — avoiding the ~1-2 day drift a
 * date-centering restore introduces.
 */
export const getPlannerContainerScrollLeft = (): number | null => {
  if (typeof document === 'undefined') return null;
  const container = document.getElementById('planner-container');
  return container ? container.scrollLeft : null;
};

/**
 * Shared reload-viewport computation for setFilters/resetFilter. A filter change
 * resets the query (planner reloads → scrollLeft snaps to 0 = first cached date),
 * so we snapshot the EXACT scrollLeft to restore afterwards — pixel-precise since
 * a filter change keeps the same dates window + cell width (no date-centering drift).
 *
 * A zero snapshot while the viewport is months past the window start means the
 * container isn't laid out / scrolled yet (e.g. a filter change racing the initial
 * scroll-to-today after a refresh); restoring 0 would jump ~6 months back. In that
 * case `isStaleZeroSnapshot` is true and the caller restores by date instead.
 */
export const computeFilterReloadViewport = (
  dates: dayjs.Dayjs[],
  visibleDateRange: VisibleDateRange,
  firstVisibleDate: dayjs.Dayjs
) => {
  const bufferedPeriod = getBufferedVisibleViewportPeriod(
    dates,
    visibleDateRange,
    firstVisibleDate
  );
  const restoreScrollLeft = getPlannerContainerScrollLeft();
  const isStaleZeroSnapshot = restoreScrollLeft === 0 && firstVisibleDate.diff(dates[0], 'day') > 2;
  const fallbackScrollTarget =
    visibleDateRange.endIndex > 0
      ? dates[Math.floor((visibleDateRange.startIndex + visibleDateRange.endIndex) / 2)]
      : firstVisibleDate;

  return { bufferedPeriod, restoreScrollLeft, isStaleZeroSnapshot, fallbackScrollTarget };
};
